import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { batch, useDispatch } from 'react-redux';
import { Logger } from '@saviynt/common';
import { Icon, type IconKind } from '@saviynt/design-system';

import {
  SodConsultActionModal,
  SodOpenActionModal,
} from '../../../../shared/components';
import {
  ALERT_BANNER_DEFAULT,
  GLOBAL_ALERT_BANNER_TYPES,
  LIST_ACTIONS,
} from '../../../../shared/constants';
import {
  useInvertedLocalize,
  useNavigateTo,
  useTypedSelector,
} from '../../../../shared/hooks';
import { globalActions } from '../../../../shared/store/globalSlice';
import { type ViolationsActionManagerState } from '../../../../shared/types';
import { downloadBlobAsFile } from '../../../../shared/utils';
import { detailsActions } from '../../../Details';
import { violationsTabActions } from '../../data-store/violationsTabSlice';
import { useActionController } from '../../hooks';

import ViolationsMcEndDate from './ViolationsMcEndDate';
import ViolationsMitigate from './ViolationsMitigate';
import ViolationsRemediateAction from './ViolationsRemediateAction';
import ViolationsRemoveMc from './ViolationsRemoveMc';

interface ViolationsListActionsManagerProps
  extends ViolationsActionManagerState {
  isDetailsPage?: boolean;
}

/**
 * ViolationsListActionsManager
 */
function ViolationsListActionsManager({
  isOpen,
  sodKey,
  action,
  onFail,
  onSuccess,
  isDetailsPage,
  isBulkAction,
  // Action Specific
  title,
  accounts,
  functions,
  startDate,
  endDate,
  riskId,
  userName,
  displayName,
  redirectProps,
  mitigatingControlCount,
  mcKeys,
}: ViolationsListActionsManagerProps) {
  const l = useInvertedLocalize();
  const dispatch = useDispatch();
  const { navigateToBack } = useNavigateTo();

  const {
    details: {
      violationsTab: { actionManager: detailsActionManager },
    },
    violationsTab: {
      list: { totalCount },
      bulkaction: { bulkCount, actionInfo },
      actionManager: violationActionManager,
    },
    global: { globalDateFormat },
  } = useTypedSelector((state) => state.aag);

  // Initializations
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Controller hook
  const { actionFn, isLoading } = useActionController({
    action,
    sodKey,
    isBulkAction,
  });

  useEffect(() => {
    if (isOpen) {
      setIsModalOpen(true);
    }
  }, [dispatch, isOpen]);

  // Constants
  const isTransition = Boolean(
    action === LIST_ACTIONS.REMOVEMC.value ||
      action === LIST_ACTIONS.REOPEN.value ||
      action === LIST_ACTIONS.MOVETOINREVIEW.value ||
      action === LIST_ACTIONS.REMEDIATE.value ||
      action === LIST_ACTIONS.MITIGATE.value
  );
  const isMcEndDate = useMemo(
    () => Boolean(action === LIST_ACTIONS.CHNAGEMCENDDATE.value),
    [action]
  );
  const isGenerateReport = useMemo(
    () => Boolean(action === LIST_ACTIONS.GENERATEREPORT.value),
    [action]
  );

  const raiseSuccessAlert = useCallback(() => {
    const { successTitle, successDescription } = LIST_ACTIONS[action];

    // For Mitigating Violations
    if (
      action === LIST_ACTIONS.MITIGATE.value ||
      action === LIST_ACTIONS.ADDMC.value
    ) {
      dispatch(
        globalActions.setGlobalAlertBanner({
          ...ALERT_BANNER_DEFAULT,
          kind: GLOBAL_ALERT_BANNER_TYPES.SUCCESS,
          title: l(successTitle ?? ''),
          description: isBulkAction
            ? l('{count} Violations have been mitigated.', { count: bulkCount })
            : l('Violation “{riskName}” has been mitigated.', {
                riskName: title,
              }),
          isOpen: true,
        })
      );

      return;
    }

    // For Remove Mitigating Controls
    if (action === LIST_ACTIONS.REMOVEMC.value) {
      if (
        mcKeys &&
        mcKeys.length === 1 &&
        mitigatingControlCount &&
        mitigatingControlCount > 1
      ) {
        dispatch(
          globalActions.setGlobalAlertBanner({
            ...ALERT_BANNER_DEFAULT,
            kind: GLOBAL_ALERT_BANNER_TYPES.SUCCESS,
            title: l(successTitle ?? ''),
            description: `Mitigating Control has been removed from the violation ${
              title ?? ''
            }`,
            isOpen: true,
          })
        );

        return;
      }

      dispatch(
        globalActions.setGlobalAlertBanner({
          ...ALERT_BANNER_DEFAULT,
          kind: GLOBAL_ALERT_BANNER_TYPES.SUCCESS,
          title: l(successTitle ?? ''),
          description: l('Violation {riskName} is moved to "Open" status', {
            riskName: title,
          }),
          isOpen: true,
        })
      );

      return;
    }

    dispatch(
      globalActions.setGlobalAlertBanner({
        ...ALERT_BANNER_DEFAULT,
        kind: GLOBAL_ALERT_BANNER_TYPES.SUCCESS,
        title: l(successTitle ?? 'Action Completed'),
        description: l(successDescription ?? '', {
          riskName: title,
        }),
        isOpen: true,
      })
    );
  }, [
    l,
    dispatch,
    action,
    mcKeys,
    mitigatingControlCount,
    title,
    isBulkAction,
    bulkCount,
  ]);

  // Handle action
  const handleSumbitAction = useCallback(
    (comment: string, config = {}) => {
      actionFn(comment, config)
        .then((response) => {
          setIsModalOpen(false);

          const typedResponse = response as { count: number; message: string };

          // Reset action manager
          dispatch(violationsTabActions.setViolationsActionManager(null));

          if (isDetailsPage && detailsActionManager) {
            dispatch(
              detailsActions.setViolationTabdetailsActionManager({
                ...detailsActionManager,
                isOpen: false,
              })
            );

            if (isMcEndDate || action === LIST_ACTIONS.ADDMC.value) {
              dispatch(
                detailsActions.setViolationTabDetailsMcListRefresh(true)
              );
            }
          }

          // Generate report
          if (isGenerateReport) {
            const suggestedFileName = (response as { fileName?: string })
              .fileName;
            const finalFileName = suggestedFileName ?? `${sodKey}.xlsx`;

            downloadBlobAsFile(response as Blob, finalFileName);

            return;
          }

          // Raising alerts
          raiseSuccessAlert();

          // Bulk action here
          if (isBulkAction) {
            dispatch(violationsTabActions.setListIsRefresh(true));
            dispatch(violationsTabActions.setIsBulkChecked(false));

            return;
          }

          // Details page specific
          if (isMcEndDate || action === LIST_ACTIONS.ADDMC.value) {
            dispatch(detailsActions.setViolationTabDetailsMcListRefresh(true));
          }

          // Individual action here
          if (isTransition) {
            // Items which will move to another tab and opened in details tab
            if (isDetailsPage) {
              navigateToBack();

              return;
            }

            dispatch(violationsTabActions.setListTransitionId(sodKey));

            //  Doing this as we are not redirecting to `targetTab`
            if (totalCount !== null) {
              dispatch(
                violationsTabActions.setListTotalCount(
                  Number(totalCount - typedResponse.count)
                )
              );
            }
          }

          // Run any function on success if any
          if (onSuccess) {
            onSuccess();
          }
        })
        .catch((error: unknown) => {
          const errorMessage =
            error instanceof Error ? error.message : String(error);

          batch(() => {
            if (isGenerateReport) {
              dispatch(violationsTabActions.setViolationsActionManager(null));

              if (isDetailsPage && detailsActionManager) {
                dispatch(
                  detailsActions.setViolationTabdetailsActionManager({
                    ...detailsActionManager,
                    isOpen: false,
                  })
                );
              }
            }

            dispatch(
              globalActions.setGlobalAlertBanner({
                ...ALERT_BANNER_DEFAULT,
                kind: GLOBAL_ALERT_BANNER_TYPES.CRITICAL,
                title: l('Action Failed'),
                description: errorMessage,
                isOpen: true,
              })
            );
          });

          if (onFail) {
            onFail();
          }

          Logger.log(errorMessage);
        });
    },
    [
      l,
      dispatch,
      actionFn,
      sodKey,
      action,
      isMcEndDate,
      isDetailsPage,
      isBulkAction,
      isGenerateReport,
      totalCount,
      isTransition,
      raiseSuccessAlert,
      onSuccess,
      onFail,
      navigateToBack,
      detailsActionManager,
    ]
  );

  const handleModalClose = useCallback(() => {
    setIsModalOpen(false);
    dispatch(violationsTabActions.setViolationsActionManager(null));

    if (isDetailsPage && detailsActionManager) {
      dispatch(
        detailsActions.setViolationTabdetailsActionManager({
          ...detailsActionManager,
          isOpen: false,
        })
      );
    }
  }, [detailsActionManager, dispatch, isDetailsPage]);

  // Generate report action
  useEffect(() => {
    if (isGenerateReport && sodKey.length > 0) {
      handleSumbitAction('', { violationId: sodKey });
    }
  }, [isGenerateReport, handleSumbitAction, sodKey]);

  // Render body
  const renderModals = useCallback(() => {
    if (isGenerateReport) {
      return <div />;
    }

    if (action === LIST_ACTIONS.REOPEN.value) {
      const { modalTitle, modalSubTitle, iconProps } = LIST_ACTIONS[action];

      return (
        <SodOpenActionModal
          isOpen={isModalOpen}
          headerIcon={
            <Icon
              kind={iconProps?.iconKind as IconKind}
              color={iconProps?.iconColor}
              size='large'
            />
          }
          title={l(modalTitle ?? 'Confirm Action?', {
            count: mitigatingControlCount,
          })}
          subtitle={modalSubTitle ? l(modalSubTitle) : ''}
          handleSumbit={handleSumbitAction}
          handleModalClose={handleModalClose}
          isLoading={isLoading}
          isDisabled={isLoading}
        />
      );
    }

    if (action === LIST_ACTIONS.REMOVEMC.value) {
      const { iconProps } = LIST_ACTIONS[action];

      return (
        <ViolationsRemoveMc
          sodKey={sodKey}
          isOpen={isModalOpen}
          headerIcon={
            <Icon
              kind={iconProps?.iconKind as IconKind}
              color={iconProps?.iconColor}
              size='large'
            />
          }
          mitigatingControlCount={mitigatingControlCount}
          mcKeys={mcKeys}
          handleSumbit={handleSumbitAction}
          handleModalClose={handleModalClose}
          isLoading={isLoading}
          isDisabled={isLoading}
        />
      );
    }

    if (
      action === LIST_ACTIONS.MOVETOINREVIEW.value ||
      action === LIST_ACTIONS.ADDREVIEWCOMMENTS.value ||
      action === LIST_ACTIONS.CHANGEREVIEWENDDATE.value
    ) {
      const { modalTitle, modalSubTitle, iconProps } = LIST_ACTIONS[action];

      return (
        <SodConsultActionModal
          isOpen={isModalOpen}
          headerIcon={
            <Icon
              kind={iconProps?.iconKind as IconKind}
              color={iconProps?.iconColor}
              size='large'
            />
          }
          title={l(modalTitle ?? 'Confirm Action?', {
            count: mitigatingControlCount,
          })}
          currentStartDate={startDate}
          currentEndDate={endDate}
          subtitle={modalSubTitle ? l(modalSubTitle) : ''}
          handleSumbit={handleSumbitAction}
          handleModalClose={handleModalClose}
          isReview={action === LIST_ACTIONS.MOVETOINREVIEW.value}
          isEndDate={action === LIST_ACTIONS.CHANGEREVIEWENDDATE.value}
          isLoading={isLoading}
          isDisabled={isLoading}
          globalDateFormat={globalDateFormat}
        />
      );
    }

    if (action === LIST_ACTIONS.REMEDIATE.value) {
      return (
        <ViolationsRemediateAction
          sodKey={sodKey}
          isOpen={isModalOpen}
          isLoading={isLoading}
          isDisabled={isLoading}
          subtitlelinkText={title ?? ''}
          functions={functions}
          accounts={accounts}
          userName={userName ?? ''}
          displayName={displayName ?? ''}
          redirectTo={redirectProps}
          handleSumbit={handleSumbitAction}
          handleModalClose={handleModalClose}
          isDetailsPage={isDetailsPage}
          violationActionManager={violationActionManager}
        />
      );
    }

    if (isMcEndDate) {
      const { modalTitle, iconProps } = LIST_ACTIONS[action];

      return (
        <ViolationsMcEndDate
          sodKey={sodKey}
          isOpen={isModalOpen}
          headerIcon={
            <Icon
              kind={iconProps?.iconKind as IconKind}
              color={iconProps?.iconColor}
              size='large'
            />
          }
          title={l(
            `${modalTitle ?? 'Confirm Action '}${
              Number(mitigatingControlCount) > 1 ? ' ({count})' : ''
            }`,
            {
              count: mitigatingControlCount,
            }
          )}
          mitigatingControlCount={mitigatingControlCount}
          mcKeys={mcKeys}
          isDetailsPage={isDetailsPage}
          handleSumbit={handleSumbitAction}
          handleModalClose={handleModalClose}
          isLoading={isLoading}
          isDisabled={isLoading}
          globalDateFormat={globalDateFormat}
        />
      );
    }

    if (
      action === LIST_ACTIONS.MITIGATE.value ||
      action === LIST_ACTIONS.ADDMC.value
    ) {
      const finalTitle =
        actionInfo.length > 0 && bulkCount > 0
          ? actionInfo[0].riskName
          : title ?? '-';

      return (
        <ViolationsMitigate
          sodKey={sodKey}
          isOpen={isModalOpen}
          riskId={riskId}
          handleSumbit={handleSumbitAction}
          handleModalClose={handleModalClose}
          title={finalTitle}
          bulkCount={bulkCount}
          isLoading={isLoading}
          isDisabled={isLoading}
          redirectProps={redirectProps}
          globalDateFormat={globalDateFormat}
          isAddMc={action === LIST_ACTIONS.ADDMC.value}
          isDetailsPage={isDetailsPage}
          violationActionManager={violationActionManager}
        />
      );
    }

    dispatch(
      globalActions.setGlobalAlertBanner({
        ...ALERT_BANNER_DEFAULT,
        kind: GLOBAL_ALERT_BANNER_TYPES.CRITICAL,
        title: l('Action Failed'),
        description: '',
        isOpen: true,
      })
    );

    return <div />;
  }, [
    l,
    dispatch,
    globalDateFormat,
    action,
    isLoading,
    isModalOpen,
    isMcEndDate,
    isGenerateReport,
    isDetailsPage,
    sodKey,
    title,
    functions,
    accounts,
    startDate,
    endDate,
    bulkCount,
    riskId,
    userName,
    displayName,
    redirectProps,
    mitigatingControlCount,
    mcKeys,
    actionInfo,
    handleSumbitAction,
    handleModalClose,
    violationActionManager,
  ]);

  return isOpen && renderModals();
}

ViolationsListActionsManager.defaultProps = {
  isDetailsPage: false,
  isBulkAction: false,
  mcKeys: undefined,
};

export default memo(ViolationsListActionsManager);
